import React, { useEffect, useRef, useState } from 'react';
import type { AdminAccountCostResponse, AdminAccountProject } from '@brand24-original/llm-types';
import {
  formatMoney,
  formatUsd,
  getAccountCosts,
} from '../../../../shared/services/admin/costs/AdminCostsService';
import './AdminUsersEditPageLegacy.scss';
import './AdminSideTabs.scss';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../shared/components/common/loader/Loader';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import type { AdminPlanTemplate } from '@brand24-original/llm-types';
import PlanTemplatesDialog from '../components/plan-templates/PlanTemplatesDialog';
import AccountPageHelp from './AccountPageHelp';
import MobileTabBarItems from '../../../../shared/components/common/mobile-tab-bar/MobileTabBarItems';
import { Dialog } from 'primereact/dialog';
import { useForm } from 'react-hook-form';
import {
  addAdminNote,
  banAccount,
  changeUserAccountData,
  deleteAdminNote,
  syncMonitoringSchedule,
  getAccountData,
  getAuditLog,
  getUserActivityLog,
  getInvitedUsers,
  getPlanTemplates,
  getProjectsForAccount,
  impersonateUser,
  unbanAccount,
  updateAdminNote,
  extendBan,
} from '../../../../shared/services/admin/users/AdminUsersService';
import { AdminNoteResponse } from '../../../../shared/models/admin/users/AdminNoteResponse';
import {
  AccountFieldChange,
  AdminAuditLogEntry,
} from '../../../../shared/models/admin/audit/AdminAuditLog';
import { UserActivityLogEntry } from '../../../../shared/models/admin/audit/UserActivityLog';
import { formatBilling } from '../../../../shared/helpers/admin/formatBilling';
import { getTokenData } from '../../../../shared/services/auth/AuthService';
import {
  IMPERSONATION_ORIGINAL_TOKEN_KEY,
  IMPERSONATION_TARGET_ID_KEY,
} from '../../../../shared/constans/constans';
import { useAuth } from '../../../../shared/contexts/auth/AuthContext';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { AccountDataResponse } from '../../../../shared/models/account/AccountData';
import { ConfirmDialog, confirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { InvitedUserResponse } from '../../../../shared/models/admin/users/InvitedUserResponse';
import { format } from 'date-fns';
import AdminInviteUser from '../components/admin-invite-user/AdminInviteUser';
import { DataTable } from '../../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { PROJECTS } from '../../../../shared/routes/path';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../../shared/components/dropdown-item/DropdownItem';
import { AccountType } from '../../../../shared/types/account/AccountType';
import { AccountStatus } from '../../../../shared/types/account/AccountStatus';
import { UserAccountData } from './models/UserAccountData';
import { Checkbox } from 'primereact/checkbox';
import { allMonitorTypes } from '../../../../shared/types/monitor/MonitorType';
import { addonDescriptions, allAddons, impliedBy } from '../../../../shared/types/account/Addons';
import {
  MONITORING_SCHEDULE_PRESETS,
  MonitoringSchedulePreset,
  presetOfInterval,
} from '../../../../shared/models/projects/MonitoringInterval';
import { ApiKeyResponse, CreateApiKeyRequest } from '../../../settings/api-keys/models/ApiKeyResponse';
import { createAccountApiKey, getAccountApiKeys } from './services/AdminAccountApiKeysService';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import useToast from '../../../../shared/hooks/global/UseToast';
import {
  AdminResource,
  getAdminPermissions,
} from '../../../../shared/models/admin/permissions/AdminPermission';
import useLayoutScrollbarWidth from '../../../../shared/hooks/scroll/UseLayoutScrollbarWidth';

// Human-readable labels for the audit-log field keys (mirrors the account edit form).
const AUDIT_FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  firstName: 'First name',
  lastName: 'Last name',
  accountStatus: 'Account status',
  trialDays: 'Trial days',
  maxUsers: 'Max users',
  questionLimit: 'Question limit',
  discountId: 'Discount ID',
  isCustomSettings: 'Custom settings',
  paymentSuspended: 'Payment suspended',
  hasBrandAccount: 'Brand account',
  llmQueryProvider: 'Query provider',
  maxGeoReportsPerMonth: 'Max GEO reports / month',
  maxAiVisibilityReportsPerMonth: 'Max AI Visibility reports / month',
  maxPromptAnalysesPerMonth: 'Max Prompt Analyst reviews / month',
  features: 'Features',
  addons: 'Addons',
  monitoringInterval: 'Monitoring frequency',
};

const auditFieldLabel = (field: string): string => AUDIT_FIELD_LABELS[field] ?? field;

// Admin audit actions — the log holds more than account edits (project reassignment, question
// deletions), so the header must follow `entry.action` rather than assume one kind of entry.
const AUDIT_ACTION_LABELS: Record<string, string> = {
  ACCOUNT_DATA_CHANGED: 'Account data changed',
  PROJECT_REASSIGNED: 'Project reassigned',
  PROJECT_UNASSIGNED: 'Project moved to another account',
  PROJECT_DELETED: 'Project deleted by admin',
  QUESTION_ADDED: 'Questions added by admin',
  QUESTION_DELETED: 'Questions deleted by admin',
  PROJECTS_SCHEDULE_SYNCED: 'Projects synced to account frequency',
  PROJECT_INDUSTRY_CATEGORY_CHANGED: 'Project industry category changed',
  ACCOUNT_BANNED: 'Account banned',
  ACCOUNT_UNBANNED: 'Account unbanned',
  ACCOUNT_BAN_EXTENDED: 'Ban extended',
};

const auditActionLabel = (action: string): string => AUDIT_ACTION_LABELS[action] ?? action;

// One-line summary for the actions that carry ids instead of a field diff.
const auditActionSummary = (entry: AdminAuditLogEntry): string => {
  const {
    projectId,
    projectName,
    newOwnerId,
    monitorIds,
    count,
    from,
    to,
    projectsPaused,
    monitorsPaused,
    reason,
    trigger,
    field,
    snippet,
    bannedUntil,
    hours,
    automatic,
  } = entry.details ?? {};
  switch (entry.action) {
    case 'ACCOUNT_BANNED': {
      const paused = `${projectsPaused ?? 0} projects and ${monitorsPaused ?? 0} prompts paused`;
      const untilText = bannedUntil
        ? `, until ${format(new Date(bannedUntil), 'yyyy-MM-dd HH:mm')}`
        : '';
      return reason === 'ABUSE'
        ? `Automatic: dangerous markup in ${trigger} (${field}): "${snippet}"${untilText} — ${paused}`
        : paused;
    }
    case 'ACCOUNT_BAN_EXTENDED':
      return `+${hours ?? 24} h, until ${
        bannedUntil ? format(new Date(bannedUntil), 'yyyy-MM-dd HH:mm') : '?'
      }`;
    case 'ACCOUNT_UNBANNED':
      return automatic ? 'Temporary ban ran out (lifted automatically)' : '';
    case 'PROJECT_INDUSTRY_CATEGORY_CHANGED': {
      const project = projectName
        ? `${projectName}${projectId ? ` (#${projectId})` : ''}`
        : `project #${projectId}`;
      return `${project}: ${from ?? 'automatic'} → ${to ?? 'automatic'}`;
    }
    case 'PROJECT_REASSIGNED':
    case 'PROJECT_UNASSIGNED': {
      const project = projectName
        ? `${projectName}${projectId ? ` (#${projectId})` : ''}`
        : projectId
        ? `project #${projectId}`
        : '';
      return [project, newOwnerId ? `→ account #${newOwnerId}` : ''].filter(Boolean).join(' ');
    }
    case 'PROJECT_DELETED':
      return projectName ? `${projectName}${projectId ? ` (#${projectId})` : ''}` : '';
    case 'QUESTION_ADDED':
    case 'QUESTION_DELETED': {
      const total = count ?? monitorIds?.length;
      const parts: string[] = [];
      if (total != null) parts.push(`${total} question${total === 1 ? '' : 's'}`);
      if (projectId) parts.push(`in project #${projectId}`);
      return parts.join(' ');
    }
    default:
      return '';
  }
};

const formatAuditValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
};

// Human-readable labels for the client's own activity actions.
const USER_ACTIVITY_LABELS: Record<string, string> = {
  PROJECT_CREATED: 'Project created',
  PROJECT_DELETED: 'Project deleted',
  QUESTION_ADDED: 'Questions added',
  QUESTION_DELETED: 'Questions deleted',
  PASSWORD_CHANGED: 'Password changed',
  NAME_CHANGED: 'Name changed',
  PLAN_CHANGED: 'Plan changed',
  SUBSCRIPTION_ACTIVATED: 'Subscription activated',
  USER_ADDED: 'User invited',
  USER_REMOVED: 'User removed',
};

const userActivityLabel = (action: string): string => USER_ACTIVITY_LABELS[action] ?? action;

// One-line description built from the entry's `details` bag (shape varies per action).
const userActivityDescription = (entry: UserActivityLogEntry): string => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = (entry.details ?? {}) as any;
  switch (entry.action) {
    case 'PROJECT_CREATED':
    case 'PROJECT_DELETED':
      return d.projectName
        ? `${d.projectName}${d.projectId ? ` (#${d.projectId})` : ''}`
        : d.projectId
        ? `#${d.projectId}`
        : '';
    case 'QUESTION_ADDED':
    case 'QUESTION_DELETED': {
      const count = d.count ?? (Array.isArray(d.monitorIds) ? d.monitorIds.length : undefined);
      const parts: string[] = [];
      if (count != null) parts.push(`${count} question${count === 1 ? '' : 's'}`);
      if (d.projectId) parts.push(`in project #${d.projectId}`);
      if (d.prompt) parts.push(`"${d.prompt}"`);
      return parts.join(' ');
    }
    case 'NAME_CHANGED': {
      const from = d.from ? `${d.from.firstName ?? ''} ${d.from.lastName ?? ''}`.trim() : '';
      const to = d.to ? `${d.to.firstName ?? ''} ${d.to.lastName ?? ''}`.trim() : '';
      return `${from || '—'} → ${to || '—'}`;
    }
    // `from` mają tylko starsze wpisy i zawsze równa się `to` (błąd źródła) — czytamy sam plan docelowy.
    case 'PLAN_CHANGED':
      return String(d.to ?? d.from ?? '—');
    case 'SUBSCRIPTION_ACTIVATED': {
      const parts: string[] = [];
      if (d.accountType) parts.push(String(d.accountType));
      if (d.price != null) parts.push(`${d.price}${d.currency ? ` ${d.currency}` : ''}`);
      if (d.subscriptionId) parts.push(`(${d.subscriptionId})`);
      return parts.join(' · ');
    }
    case 'USER_ADDED':
      return d.email ? `${d.email}${d.role ? ` (${d.role})` : ''}` : '';
    case 'USER_REMOVED':
      return d.email ? String(d.email) : d.removedUserId ? `#${d.removedUserId}` : '';
    default:
      return '';
  }
};

const AdminUsersEditPageLegacy: React.FC<{ onSwitchLayout: () => void }> = ({ onSwitchLayout }) => {
  const { t } = useTranslation();
  // Edge tabs sit next to the page scrollbar, never on it.
  const sideTabsOffset = useLayoutScrollbarWidth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { accountResponse, refetchData } = useAccount();
  // Read-only admins can browse the account but not mutate it (backend enforces this too).
  const canEdit = getAdminPermissions(accountResponse)[AdminResource.ACCOUNTS].write;
  const templatePermission = getAdminPermissions(accountResponse)[AdminResource.PLAN_TEMPLATES];
  const canSeeCosts = getAdminPermissions(accountResponse)[AdminResource.COSTS].read;
  const [accountCosts, setAccountCosts] = useState<AdminAccountCostResponse | null>(null);
  const [invitedUser, setInvitedUser] = useState<InvitedUserResponse[] | null>(null);
  const [userData, setUserData] = useState<AccountDataResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [range, setRange] = useState<Date[] | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  // Mirrors resolveAiVisibilityMonthlyLimit in llm-common — keep the numbers in sync.
  const aiVisibilityDefaultLimit = accountStatus === 'TRIAL' ? 2 : 5;
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [isCustomSettings, setCustomSettings] = useState<boolean>(false);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  // Plan templates: picking one only ticks the boxes below — "Change data" saves as usual.
  const [planTemplates, setPlanTemplates] = useState<AdminPlanTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState<boolean>(false);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const [paymentSuspended, setPaymentSuspended] = useState<boolean>(false);
  const [hasBrandAccount, setHasBrandAccount] = useState<boolean>(false);
  const [isSemrushProvider, setSemrushProvider] = useState<boolean>(false);
  const [monitoringPreset, setMonitoringPreset] = useState<MonitoringSchedulePreset>('DAILY');
  const [projectsOffSchedule, setProjectsOffSchedule] = useState<number>(0);
  const [syncingSchedule, setSyncingSchedule] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[] | null>(null);
  const [auditLog, setAuditLog] = useState<AdminAuditLogEntry[]>([]);
  const [userActivity, setUserActivity] = useState<UserActivityLogEntry[]>([]);
  const { showToast, toastElement } = useToast();
  const [projectsForId, setProjectsForId] = useState<AdminAccountProject[]>([]);
  const [adminNotes, setAdminNotes] = useState<AdminNoteResponse[]>([]);
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [showAllNotes, setShowAllNotes] = useState<boolean>(false);
  const NOTES_INITIAL_VISIBLE = 5;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  let confirmRef: ConfirmDialogReturn;
  const calendarRef = useRef<Calendar>(null);
  const accountStatusOpt: { name: string; type: AccountStatus }[] = [
    { name: 'Paid', type: 'PAID' },
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Overdue', type: 'OVERDUE' },
    { name: 'Blocked', type: 'BLOCKED' },
    { name: 'Disabled', type: 'DISABLED' },
  ];
  const accountTypeOpt: { name: string; type: AccountType }[] = [
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Standard', type: 'STANDARD' },
    { name: 'Pro', type: 'PRO' },
    { name: 'Advanced', type: 'ADVANCED' },
    { name: 'Enterprise', type: 'ENTERPRISE' },
  ];
  const monitoringScheduleOpt: { name: string; preset: MonitoringSchedulePreset }[] = [
    { name: 'Daily', preset: 'DAILY' },
    { name: 'Weekly', preset: 'WEEKLY' },
  ];
  // The button syncs to the SAVED frequency and says so in its label, so an unsaved dropdown change
  // does not block it; only "nothing differs" does.
  const savedScheduleName =
    monitoringScheduleOpt.find((o) => o.preset === presetOfInterval(userData?.monitoringInterval))
      ?.name ?? 'Daily';
  // The label carries the count of projects on a different frequency; 0 disables the button.
  const canSyncSchedule = canEdit && projectsOffSchedule > 0 && !syncingSchedule;
  const syncScheduleTooltip = projectsOffSchedule === 0 ? 'All projects already use this frequency' : '';

  const syncProjectsSchedule = async () => {
    setSyncingSchedule(true);
    try {
      const { projectsSynced } = await syncMonitoringSchedule(Number(id));
      setProjectsOffSchedule(0);
      showToast(`Synced ${projectsSynced} project(s) to the account frequency`);
    } catch (error: any) {
      showToast(error.response?.data?.error || 'Failed to sync projects', 'error');
    } finally {
      setSyncingSchedule(false);
    }
  };

  const clearApiError = () => {
    setApiError(null);
  };

  const successDialog = () => {
    confirmDialog({
      message: 'Data successfully saved',
      header: t('dataSaved'),
      defaultFocus: 'accept',
      acceptLabel: 'OK',
      className: 'dialog-box dialog-box--accept',
      accept: () => window.location.reload(),
    });
  };

  const hideDialog = () => {
    if (confirmRef) {
      confirmRef.hide();
    }

    window.location.reload();
  };
  const inviteUser = () => {
    confirmRef = confirmDialog({
      message: <AdminInviteUser accountId={Number(id)} afterSave={hideDialog} />,
      header: 'Invite user',
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  useEffect(() => {
    if (!canSeeCosts) return;
    getAccountCosts(Number(id))
      .then(setAccountCosts)
      .catch(() => setAccountCosts(null));
  }, [id, canSeeCosts]);

  useEffect(() => {
    if (templatePermission.read) getPlanTemplates().then(setPlanTemplates);
  }, [templatePermission.read]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          accountResponse,
          invitedUserResponse,
          apiKeysResponse,
          projects,
          auditLogResponse,
          userActivityResponse,
        ] = await Promise.all([
          getAccountData(Number(id)),
          getInvitedUsers(Number(id)),
          getAccountApiKeys(Number(id)),
          getProjectsForAccount(Number(id)),
          getAuditLog(Number(id)),
          getUserActivityLog(Number(id)),
        ]);

        setProjectsForId(projects);
        setAuditLog(auditLogResponse);
        setUserActivity(userActivityResponse);

        setUserData(accountResponse);
        setInvitedUser(invitedUserResponse);
        setApiKeys(apiKeysResponse);
        setAccountStatus(accountResponse.accountStatus);
        setAccountType(accountResponse.accountType);
        setCustomSettings(accountResponse.isCustomSettings);
        setPaymentSuspended(accountResponse.paymentSuspended || false);
        setHasBrandAccount(accountResponse.hasBrandAccount || false);
        setSemrushProvider(accountResponse.llmQueryProvider === 'SEMRUSH');
        setSelectedAddons(accountResponse.addons || []);
        setSelectedFeatures(accountResponse.features || []);
        setMonitoringPreset(presetOfInterval(accountResponse.monitoringInterval));
        setProjectsOffSchedule(accountResponse.projectsOffScheduleCount ?? 0);
        setAdminNotes(accountResponse.adminNotes ?? []);

        const startDate = new Date(accountResponse.created);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + accountResponse.trialDays);
        setRange([startDate, endDate]);
      } catch (error) {
        console.error('Failed to load account data:', error);
      }
    };

    loadData();
  }, [id]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('copied'));
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const loadAccountApiKeys = async () => {
    try {
      const apiKeysResponse = await getAccountApiKeys(Number(id));
      setApiKeys(apiKeysResponse);
    } catch (error) {
      console.error('Failed to load API keys for account:', error);
    }
  };

  const getKeyStatus = (expires: Date | null | undefined) => {
    if (!expires) {
      return { status: 'active', label: t('neverExpires') };
    }

    const now = new Date();
    const expiresDate = new Date(expires);
    const daysUntilExpiry = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (now > expiresDate) {
      return { status: 'expired', label: t('expired') };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expires-soon', label: t('expiresInDays', { days: daysUntilExpiry }) };
    } else {
      return { status: 'active', label: t('expiresInDays', { days: daysUntilExpiry }) };
    }
  };

  const addNewAccountApiKey = () => {
    confirmRef = confirmDialog({
      message: (
        <AdminApiKeyCreateForm
          accountId={Number(id)}
          afterSave={async () => {
            if (confirmRef) {
              confirmRef.hide();
            }
            await loadAccountApiKeys();
          }}
        />
      ),
      header: t('createApiKey'),
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const AdminApiKeyCreateForm: React.FC<{ accountId: number; afterSave: () => void }> = ({
    accountId,
    afterSave,
  }) => {
    const [localApiError, setLocalApiError] = useState<string | null>(null);
    const {
      register: registerApiKey,
      handleSubmit: handleSubmitApiKey,
      formState: { errors: apiKeyErrors },
    } = useForm();

    const validityOptions = [
      { label: t('never'), value: undefined },
      { label: t('30Days'), value: 30 },
      { label: t('90Days'), value: 90 },
      { label: t('180Days'), value: 180 },
      { label: t('365Days'), value: 365 },
    ];

    const onSubmitApiKey = async (formData: any) => {
      try {
        setLocalApiError(null);
        const request: CreateApiKeyRequest = {
          validForDays: formData.validForDays ? parseInt(formData.validForDays, 10) : undefined,
          description: formData.description || undefined,
        };
        await createAccountApiKey(accountId, request);
        afterSave();
      } catch (error: any) {
        console.error('Failed to create API key for account:', error);
        setLocalApiError(error.response?.data?.error || t('createApiKeyError'));
      }
    };

    const clearLocalApiError = () => {
      setLocalApiError(null);
    };

    return (
      <div>
        <form onSubmit={handleSubmitApiKey(onSubmitApiKey)} className="form-box__form">
          <div className="form-box__row">
            <div className="form-box__label">{t('description')}:</div>
            <InputText
              onInput={clearLocalApiError}
              {...registerApiKey('description')}
              placeholder={t('enterDescription')}
              className={apiKeyErrors.description || localApiError ? 'form-box__invalid' : ''}
            />
            {apiKeyErrors.description && <span>{apiKeyErrors.description.message as string}</span>}
          </div>

          <div className="form-box__row">
            <div className="form-box__label">{t('validityPeriod')}:</div>
            <Dropdown
              {...registerApiKey('validForDays')}
              options={validityOptions}
              placeholder={t('selectValidityPeriod')}
              className="dropdown"
              optionLabel="label"
              optionValue="value"
            />
          </div>

          {localApiError && <div className="p-error">{localApiError}</div>}

          <Button className="btn btn--sm" label={t('create')} type="submit" />
        </form>
      </div>
    );
  };

  const handleAddNote = async () => {
    if (!id) return;
    const trimmed = newNoteContent.trim();
    if (trimmed.length === 0) return;
    setIsAddingNote(true);
    try {
      const created = await addAdminNote(Number(id), trimmed);
      setAdminNotes((prev) => [created, ...prev]);
      setNewNoteContent('');
    } catch (error) {
      console.error('Failed to add admin note', error);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleStartEditNote = (note: AdminNoteResponse) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEditNote = async () => {
    if (!id || editingNoteId == null) return;
    const trimmed = editingContent.trim();
    if (trimmed.length === 0) return;
    setIsSavingEdit(true);
    try {
      const updated = await updateAdminNote(Number(id), editingNoteId, trimmed);
      setAdminNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)));
      setEditingNoteId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Failed to update admin note', error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!id) return;
    // Snapshot the row by id, not the whole array — if another note is added
    // mid-flight, a wholesale array rollback would drop it.
    const snapshot = adminNotes.find((n) => n.id === noteId);
    setAdminNotes((prev) => prev.filter((n) => n.id !== noteId));
    try {
      await deleteAdminNote(Number(id), noteId);
    } catch (error) {
      console.error('Failed to delete admin note', error);
      if (snapshot) {
        setAdminNotes((curr) => (curr.some((n) => n.id === noteId) ? curr : [...curr, snapshot]));
      }
    }
  };

  const handleOpenPaddle = () => {
    if (!userData?.subscriptionId) return;
    window.open(
      `https://vendors.paddle.com/subscriptions-v2/${encodeURIComponent(userData.subscriptionId)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleOpenIntercom = () => {
    if (!userData?.intercomUrl) return;
    window.open(userData.intercomUrl, '_blank', 'noopener,noreferrer');
  };

  const isBanned = Boolean(userData?.bannedAt);

  const handleExtendBan = () => {
    if (!id) return;
    confirmDialog({
      message:
        'Extend the temporary ban by 24 hours? The account stays blocked and its projects paused until the new end.',
      header: 'Extend ban',
      defaultFocus: 'accept',
      acceptLabel: 'Extend 24 h',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: async () => {
        try {
          await extendBan(Number(id));
          window.location.reload();
        } catch (error) {
          console.error('Failed to extend ban', error);
        }
      },
    });
  };

  const handleToggleBan = () => {
    if (!id) return;
    confirmDialog({
      message: isBanned
        ? 'Lift the ban? The prompts paused by the ban will resume monitoring.'
        : 'Ban this account? The user will only see a suspension screen and every project and prompt will be paused.',
      header: isBanned ? 'Unban account' : 'Ban account',
      defaultFocus: 'reject',
      acceptLabel: isBanned ? 'Unban' : 'Ban',
      rejectLabel: t('cancel'),
      className: 'dialog-box',
      accept: async () => {
        try {
          await (isBanned ? unbanAccount(Number(id)) : banAccount(Number(id)));
          window.location.reload();
        } catch (error) {
          console.error('Failed to change ban state', error);
        }
      },
    });
  };

  const handleImpersonate = async () => {
    if (!id) return;
    try {
      const originalTokenData = getTokenData();
      if (originalTokenData) {
        sessionStorage.setItem(IMPERSONATION_ORIGINAL_TOKEN_KEY, JSON.stringify(originalTokenData));
        sessionStorage.setItem(IMPERSONATION_TARGET_ID_KEY, id);
      }
      const tokenData = await impersonateUser(Number(id));
      login(tokenData);
      refetchData();
      navigate(PROJECTS);
    } catch (error) {
      console.error('Failed to impersonate user', error);
    }
  };

  const onSubmit = async (formData: any) => {
    const userAccount = {
      accountId: Number(id),
      questionLimit: Number(formData.questionLimit),
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      trialDays: Number(daysBetween),
      maxUsers: Number(formData.maxUsers),
      maxGeoReportsPerMonth: formData.maxGeoReportsPerMonth
        ? Number(formData.maxGeoReportsPerMonth)
        : null,
      // '' -> null (use the default). '0' must survive as 0 (blocked), so no truthiness check here.
      maxAiVisibilityReportsPerMonth:
        String(formData.maxAiVisibilityReportsPerMonth ?? '').trim() === ''
          ? null
          : Number(formData.maxAiVisibilityReportsPerMonth),
      maxPromptAnalysesPerMonth:
        String(formData.maxPromptAnalysesPerMonth ?? '').trim() === ''
          ? null
          : Number(formData.maxPromptAnalysesPerMonth),
      accountStatus,
      accountType,
      discountId: formData.discountId,
      isCustomSettings,
      paymentSuspended,
      hasBrandAccount,
      llmQueryProvider: isSemrushProvider ? 'SEMRUSH' : null,
      addons: selectedAddons,
      features: selectedFeatures,
      monitoringInterval: MONITORING_SCHEDULE_PRESETS[monitoringPreset],
    } as UserAccountData;

    if (
      isNaN(daysBetween) ||
      isNaN(userAccount.questionLimit) ||
      isNaN(userAccount.maxUsers) ||
      isNaN(userAccount.accountId)
    ) {
      setApiError('Wrong data');
      return;
    }

    await changeUserAccountData(userAccount)
      .then(() => {
        successDialog();
      })
      .catch((err) => {
        if (err.statusCode === 401) {
          return;
        }

        setApiError(err.response.data.error);
      });
  };

  if (!projectsForId || !userData || invitedUser === null || !range || !apiKeys) {
    return (
      <div className="admin-edit-user-legacy">
        <Loader />
      </div>
    );
  }

  const daysBetween =
    range[0] && range[1]
      ? Math.max(1, Math.ceil((range[1].getTime() - range[0].getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

  // The client's own activity log — rendered below the admin "Activity log" at the bottom of the page.
  const renderUserActivity = () => (
    <div className="admin-edit-user-legacy__audit-log">
      <div className="page-box__title-action">
        <div className="page-box__title">User activity ({userActivity.length})</div>
      </div>
      {userActivity.length === 0 ? (
        <div className="admin-edit-user-legacy__audit-empty">No activity recorded yet.</div>
      ) : (
        <ul className="admin-edit-user-legacy__audit-list">
          {userActivity.map((entry) => {
            const description = userActivityDescription(entry);
            return (
              <li key={entry.id} className="admin-edit-user-legacy__audit-item">
                <div className="admin-edit-user-legacy__audit-head">
                  <span className="admin-edit-user-legacy__audit-action">
                    {userActivityLabel(entry.action)}
                    {entry.isAdminAction && (
                      <span className="admin-edit-user-legacy__audit-badge">admin</span>
                    )}
                  </span>
                  <span className="admin-edit-user-legacy__audit-date">
                    {format(new Date(entry.created), 'yyyy-MM-dd HH:mm')}
                  </span>
                </div>
                <div className="admin-edit-user-legacy__audit-actor">
                  by {entry.actorEmail || `user #${entry.actorUserId}`}
                  {entry.isAdminAction && ' (superadmin, not the client)'}
                </div>
                {description && (
                  <ul className="admin-edit-user-legacy__audit-changes">
                    <li className="admin-edit-user-legacy__audit-change">{description}</li>
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div className="admin-edit-user-legacy">
      <ConfirmDialog className="dialog-box" />
      <Dialog
        header="Help"
        visible={helpOpen}
        onHide={() => setHelpOpen(false)}
        className="dialog-box admin-edit-user-legacy__help-dialog"
      >
        <AccountPageHelp />
      </Dialog>
      <MobileTabBarItems>
        <button type="button" className="mobile-tab-bar__item" onClick={() => setHelpOpen(true)}>
          <span className="ico--question ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">Help</span>
        </button>
        <button type="button" className="mobile-tab-bar__item" onClick={onSwitchLayout}>
          <span className="ico--grid-3x3 ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">New view</span>
        </button>
      </MobileTabBarItems>
      <div className="admin-side-tabs" style={{ right: sideTabsOffset }}>
        <button
          type="button"
          className="admin-side-tabs__tab"
          title="Help"
          onClick={() => setHelpOpen(true)}
        >
          <span className="ico ico--md ico--question admin-side-tabs__icon" />
          Help
        </button>
        <button
          type="button"
          className="admin-side-tabs__tab admin-side-tabs__tab--muted"
          title="Try the new account page"
          onClick={onSwitchLayout}
        >
          <span className="ico ico--md ico--grid-3x3 admin-side-tabs__icon" />
          New view
        </button>
      </div>
      <PlanTemplatesDialog
        visible={templatesDialogOpen}
        onHide={() => setTemplatesDialogOpen(false)}
        canEdit={templatePermission.write}
        onChanged={setPlanTemplates}
        initialFeatures={selectedFeatures}
        initialAddons={selectedAddons}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        {!canEdit && (
          <div className="admin-edit-user-legacy__readonly-banner">
            Read-only access — you can view this account but not edit it.
          </div>
        )}
        {userData.bannedAt && (
          <div className="admin-edit-user-legacy__banned-banner">
            Account banned since {format(new Date(userData.bannedAt), 'yyyy-MM-dd HH:mm')}
            {userData.bannedReason === 'ABUSE' ? ' automatically (dangerous markup in user input)' : ''}
            {userData.bannedUntil
              ? ` until ${format(new Date(userData.bannedUntil), 'yyyy-MM-dd HH:mm')}`
              : ''}{' '}
            — the user sees only the suspension screen; all projects are paused.
          </div>
        )}
        <fieldset disabled={!canEdit} className="admin-edit-user-legacy__form-fieldset">
          <div className="page-box__title-action">
            <div className="page-box__title">
              Account data
              <div className="admin-edit-user-legacy__header-actions">
                {accountResponse?.isSuperAdmin &&
                  canEdit &&
                  Number(id) !== accountResponse?.accountId && (
                    <Button
                      className="btn btn--sm admin-edit-user-legacy__impersonate-btn"
                      label={t('loginAsUser')}
                      type="button"
                      onClick={handleImpersonate}
                    />
                  )}
                {userData.intercomUrl && (
                  <Button
                    className="btn btn--sm"
                    label="Open in Intercom"
                    type="button"
                    onClick={handleOpenIntercom}
                  />
                )}
                {canEdit && Number(id) !== accountResponse?.accountId && (
                  <Button
                    className={`btn btn--sm ${
                      isBanned ? 'admin-edit-user-legacy__unban-btn' : 'admin-edit-user-legacy__ban-btn'
                    }`}
                    label={isBanned ? 'Unban account' : 'Ban account'}
                    type="button"
                    onClick={handleToggleBan}
                  />
                )}
                {canEdit && isBanned && userData.bannedUntil && (
                  <Button
                    className="btn btn--sm"
                    label="Extend ban 24 h"
                    type="button"
                    onClick={handleExtendBan}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="form">
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Email:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.email)}
                  {...register('email', { required: t('fieldIsRequired') })}
                  className={errors.email || apiError ? 'form-box__invalid' : ''}
                />
                {errors.email && <span>{errors.email.message as string}</span>}
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">First name:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.firstName)}
                  {...register('firstName')}
                  className={errors.firstName || apiError ? 'form-box__invalid' : ''}
                />
                {errors.firstName && <span>{errors.firstName.message as string}</span>}
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Last name:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.lastName)}
                  {...register('lastName')}
                  className={errors.lastName || apiError ? 'form-box__invalid' : ''}
                />
                {errors.lastName && <span>{errors.lastName.message as string}</span>}
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Discount id:</div>
                <InputText
                  onInput={clearApiError}
                  {...register('discountId')}
                  defaultValue={userData.discountId || ''}
                  className={apiError ? 'form-box__invalid' : ''}
                />
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setHasBrandAccount(!hasBrandAccount)}
                    checked={hasBrandAccount}
                  />
                  <span>Has Brand24 account</span>
                </div>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Account status:</div>
                <Dropdown
                  onChange={(e) => {
                    setAccountStatus(e.value.type);
                  }}
                  value={accountStatusOpt.find((o) => o.type === accountStatus)}
                  className="dropdown dropdown--disabled"
                  optionLabel="name"
                  options={accountStatusOpt}
                  itemTemplate={(option) => <DropdownItem name={option.name} />}
                />
                <div className="legend">
                  PAID (aktywne płacące)
                  <br />
                  TRIAL (aktywne trial)
                  <br />
                  OVERDUE (przekroczony termin zapłaty działa normalnie)
                  <br />
                  BLOCKED (przekroczony termin user nie może nic zrobić, w tle działa normalnie )<br />
                  DISABLED (zablokowane)
                </div>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Account type:</div>
                <Dropdown
                  onChange={(e) => {
                    setAccountType(e.value.type);
                  }}
                  value={accountTypeOpt.find((o) => o.type === accountType)}
                  options={accountTypeOpt}
                  className="dropdown"
                  optionLabel="name"
                  itemTemplate={(option) => <DropdownItem name={option.name} />}
                  disabled={true}
                />
                <div className="legend">
                  TRIAL 20 questions / 2 users
                  <br />
                  STANDARD 10 questions / 1 user
                  <br />
                  PRO 30 questions / 2 users
                  <br />
                  ADVANCED 100 questions / 10 users
                  <br />
                  ENTERPRISE custom questions / custom users
                </div>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Monitoring frequency:</div>
                <div className="admin-edit-user-legacy__schedule">
                  <Dropdown
                    onChange={(e) => setMonitoringPreset(e.value.preset)}
                    value={monitoringScheduleOpt.find((o) => o.preset === monitoringPreset)}
                    options={monitoringScheduleOpt}
                    className="dropdown"
                    optionLabel="name"
                    itemTemplate={(option) => <DropdownItem name={option.name} />}
                  />
                  <span title={syncScheduleTooltip}>
                    <Button
                      type="button"
                      className="btn btn--sm"
                      label={`Sync projects to ${savedScheduleName} (${projectsOffSchedule})`}
                      disabled={!canSyncSchedule}
                      loading={syncingSchedule}
                      onClick={syncProjectsSchedule}
                    />
                  </span>
                </div>
                <small className="form-box__hint">
                  Default frequency for new projects. Without the MANAGE_MONITORING_SCHEDULE addon it
                  also <strong>applies to every existing project</strong> on save; with the addon,
                  existing projects keep their own schedule.
                </small>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Trial expiration:</div>
                <div className="calendar">
                  <Calendar
                    ref={calendarRef}
                    value={range}
                    onChange={(e) => {
                      const endDate = (e.value as Date[])[1] || (e.value as Date[])[0];
                      setRange([new Date(userData.created), endDate]);
                      if (endDate && calendarRef.current) {
                        calendarRef.current.hide();
                      }
                    }}
                    selectionMode="range"
                    readOnlyInput
                    hideOnRangeSelection
                    dateFormat="yy-mm-dd"
                    pt={{
                      root: {
                        className: 'calendar',
                      },
                      panel: {
                        className: 'calendar-panel',
                      },
                    }}
                    minDate={new Date(userData.created)}
                  />
                </div>
                <InputText
                  value={String(daysBetween)}
                  className={errors.trialDays || apiError ? 'form-box__invalid' : ''}
                  disabled={true}
                />
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Question limit:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.maxMonitorCount)}
                  {...register('questionLimit', { required: t('fieldIsRequired') })}
                  className={errors.questionLimit || apiError ? 'form-box__invalid' : ''}
                />
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">User limit:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.maxUsers)}
                  {...register('maxUsers', { required: t('fieldIsRequired') })}
                  className={errors.maxUsers || apiError ? 'form-box__invalid' : ''}
                />
                {errors.maxUsers && <span>{errors.maxUsers.message as string}</span>}
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">GEO reports / month:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.maxGeoReportsPerMonth ?? '')}
                  {...register('maxGeoReportsPerMonth')}
                  className={apiError ? 'form-box__invalid' : ''}
                  placeholder="Default (trial: 3, paid: 5)"
                />
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">AI Visibility reports / month:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.maxAiVisibilityReportsPerMonth ?? '')}
                  {...register('maxAiVisibilityReportsPerMonth')}
                  className={apiError ? 'form-box__invalid' : ''}
                  placeholder={`Empty = default for this account (${aiVisibilityDefaultLimit})`}
                />
                {/* Follows the status dropdown above, so switching TRIAL <-> paid updates the hint. */}
                <small className="form-box__hint">
                  Empty = default for this account ({aiVisibilityDefaultLimit}
                  {accountStatus === 'TRIAL' ? ', trial' : ''}) · 0 = generating blocked · any other
                  number overrides the limit.
                </small>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Prompt Analyst reviews / month:</div>
                <InputText
                  onInput={clearApiError}
                  defaultValue={String(userData.maxPromptAnalysesPerMonth ?? '')}
                  {...register('maxPromptAnalysesPerMonth')}
                  className={apiError ? 'form-box__invalid' : ''}
                  placeholder="Empty = default (20)"
                />
                <small className="form-box__hint">
                  Empty = default (20) · 0 = blocked · any other number overrides the limit.
                </small>
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Account settings:</div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setCustomSettings(!isCustomSettings)}
                    checked={isCustomSettings}
                  />
                  <span>Custom settings</span>
                </div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setPaymentSuspended(!paymentSuspended)}
                    checked={paymentSuspended}
                  />
                  <span>{t('paymentSuspended')}</span>
                </div>
                <div className="form-box__checkbox">
                  <Checkbox
                    onChange={() => setSemrushProvider(!isSemrushProvider)}
                    checked={isSemrushProvider}
                  />
                  <span>Query models via SemRush (unsupported models stay on BrightData)</span>
                </div>
              </div>
            </div>
            {templatePermission.read && (
              <div className="form__row">
                <div className="form-box__col">
                  <div className="form-box__label">Plan template:</div>
                  <div className="admin-edit-user-legacy__schedule">
                    <Dropdown
                      value={selectedTemplateId}
                      onChange={(e) => setSelectedTemplateId(e.value ?? null)}
                      options={planTemplates.map((template) => ({
                        label: `${template.name} · ${template.features.length} models, ${template.addons.length} addons`,
                        value: template.id,
                      }))}
                      placeholder={planTemplates.length ? 'Pick a template…' : 'No templates yet'}
                      className="dropdown"
                      showClear
                      itemTemplate={(option) => <DropdownItem name={option.label} />}
                    />
                    <span>
                      <Button
                        type="button"
                        className="btn btn--sm"
                        label="Apply"
                        disabled={selectedTemplateId === null}
                        onClick={() => {
                          const template = planTemplates.find((item) => item.id === selectedTemplateId);
                          if (!template) return;
                          setSelectedFeatures(template.features);
                          setSelectedAddons(template.addons);
                        }}
                      />
                    </span>
                    {templatePermission.write && (
                      <span>
                        <Button
                          type="button"
                          className="btn btn--sm btn--secondary"
                          label="Manage…"
                          onClick={() => setTemplatesDialogOpen(true)}
                        />
                      </span>
                    )}
                  </div>
                  <small className="form-box__hint">
                    Apply ticks the template's models and addons below; nothing is saved until "Change
                    data".
                  </small>
                </div>
              </div>
            )}
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Models:</div>
                {allMonitorTypes.map((model) => (
                  <div className="form-box__checkbox">
                    <Checkbox
                      checked={selectedFeatures.includes(model)}
                      onChange={(e) => {
                        if (e.checked) {
                          setSelectedFeatures((prev) => [...prev, model]);
                        } else {
                          setSelectedFeatures((prev) => prev.filter((f) => f !== model));
                        }
                      }}
                    />
                    <span>{model}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="form__row">
              <div className="form-box__col">
                <div className="form-box__label">Addons:</div>
                {allAddons.map((addon) => {
                  // An implied addon is granted by the server on save whatever the box says, so the box
                  // shows it ticked and locked instead of letting the admin untick something that comes back.
                  const implier = impliedBy(addon, selectedAddons);
                  return (
                    <div key={addon} className="form-box__checkbox admin-edit-user-legacy__addon">
                      <Checkbox
                        checked={selectedAddons.includes(addon) || implier !== null}
                        disabled={implier !== null}
                        onChange={(e) => {
                          if (e.checked) {
                            setSelectedAddons((prev) => [...prev, addon]);
                          } else {
                            setSelectedAddons((prev) => prev.filter((f) => f !== addon));
                          }
                        }}
                      />
                      <span className="admin-edit-user-legacy__addon-text">
                        <span className="admin-edit-user-legacy__addon-name">{addon}</span>
                        <span className="admin-edit-user-legacy__addon-desc">
                          {addonDescriptions[addon]}
                          {implier && ` Implied by ${implier}.`}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {canEdit && <Button className="btn btn--sm" label="Change data" type="submit" />}
          </div>
        </fieldset>
      </form>
      <div className="admin-edit-user-legacy__right">
        <div className="admin-edit-user-legacy__account-summary">
          <div className="page-box__title-action">
            <div className="page-box__title">Subscription & limits</div>
          </div>
          <div className="admin-edit-user-legacy__summary-grid">
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Account type</div>
              <div className="admin-edit-user-legacy__summary-value">{userData.accountType}</div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Billing cycle</div>
              <div className="admin-edit-user-legacy__summary-value">
                {formatBilling(userData.billingInterval)}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Discount</div>
              <div className="admin-edit-user-legacy__summary-value">
                {userData.discount && userData.discount !== '-' ? userData.discount : '-'}
                {userData.discountId ? ` (${userData.discountId})` : ''}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Created</div>
              <div className="admin-edit-user-legacy__summary-value">
                {userData.created ? format(new Date(userData.created), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">End date</div>
              <div className="admin-edit-user-legacy__summary-value">
                {userData.endsAt ? format(new Date(userData.endsAt), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Last login</div>
              <div className="admin-edit-user-legacy__summary-value">
                {userData.lastLogin ? format(new Date(userData.lastLogin), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Login count</div>
              <div className="admin-edit-user-legacy__summary-value">{userData.loginCount ?? 0}</div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Projects</div>
              <div className="admin-edit-user-legacy__summary-value">{userData.projectsCount}</div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Prompts</div>
              <div className="admin-edit-user-legacy__summary-value">
                {(userData.accountType as string) === 'PAID'
                  ? `${userData.monitorCount} / unlimited`
                  : `${userData.monitorCount} / ${userData.maxMonitorCount}`}
              </div>
            </div>
            <div className="admin-edit-user-legacy__summary-cell">
              <div className="admin-edit-user-legacy__summary-label">Users</div>
              <div className="admin-edit-user-legacy__summary-value">{`${userData.usersCount} / ${userData.maxUsers}`}</div>
            </div>
          </div>
          {userData.subscriptionId && (
            <div className="admin-edit-user-legacy__summary-actions">
              <Button
                className="btn btn--sm"
                label="Open in Paddle"
                type="button"
                onClick={handleOpenPaddle}
              />
              <span className="admin-edit-user-legacy__subscription-id">{userData.subscriptionId}</span>
            </div>
          )}
        </div>

        {canSeeCosts && (
          <div className="admin-edit-user-legacy__account-summary">
            <div className="page-box__title-action">
              <div
                className="page-box__title beta-header"
                title="Beta — estimated from metered usage and list prices"
              >
                This month: pays vs costs us <span className="indicator indicator--beta">Beta</span>
              </div>
            </div>
            {accountCosts ? (
              <>
                <div className="admin-edit-user-legacy__summary-grid">
                  <div className="admin-edit-user-legacy__summary-cell">
                    <div className="admin-edit-user-legacy__summary-label">Pays / month</div>
                    <div className="admin-edit-user-legacy__summary-value">
                      {formatMoney(
                        accountCosts.billing?.monthlyMinor ?? null,
                        accountCosts.billing?.currencyCode ?? null
                      )}
                    </div>
                  </div>
                  <div className="admin-edit-user-legacy__summary-cell">
                    <div className="admin-edit-user-legacy__summary-label">
                      Costs us ({accountCosts.month.slice(0, 7)})
                    </div>
                    <div className="admin-edit-user-legacy__summary-value">
                      {formatUsd(accountCosts.totalUsd)}
                    </div>
                  </div>
                  <div className="admin-edit-user-legacy__summary-cell">
                    <div className="admin-edit-user-legacy__summary-label">Projected full month</div>
                    <div className="admin-edit-user-legacy__summary-value">
                      {accountCosts.projectedTotalUsd === null
                        ? '-'
                        : `≈ ${formatUsd(accountCosts.projectedTotalUsd)}`}
                    </div>
                  </div>
                  <div className="admin-edit-user-legacy__summary-cell">
                    <div className="admin-edit-user-legacy__summary-label">Margin</div>
                    <div className="admin-edit-user-legacy__summary-value">
                      {accountCosts.marginUsd === null ? '-' : formatUsd(accountCosts.marginUsd)}
                    </div>
                  </div>
                  <div className="admin-edit-user-legacy__summary-cell">
                    <div className="admin-edit-user-legacy__summary-label">Plan</div>
                    <div className="admin-edit-user-legacy__summary-value">
                      {accountCosts.billing
                        ? accountCosts.billing.items.map((i) => i.productSlug ?? i.priceId).join(', ') ||
                          '-'
                        : 'not synced'}
                    </div>
                  </div>
                </div>
                {accountCosts.byProvider.length > 0 && (
                  <div className="admin-edit-user-legacy__summary-grid">
                    {accountCosts.byProvider.map((p) => (
                      <div className="admin-edit-user-legacy__summary-cell" key={p.key}>
                        <div className="admin-edit-user-legacy__summary-label">{p.key}</div>
                        <div className="admin-edit-user-legacy__summary-value">
                          {formatUsd(p.costUsd)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="admin-edit-user-legacy__summary-label">No cost data</div>
            )}
          </div>
        )}

        <div className="admin-edit-user-legacy__admin-note">
          <div className="page-box__title-action">
            <div className="page-box__title">Admin notes ({adminNotes.length})</div>
          </div>

          <div className="admin-edit-user-legacy__admin-note-form">
            <textarea
              className="admin-edit-user-legacy__admin-note-textarea"
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Internal note visible to admins only"
              maxLength={5000}
              rows={2}
              disabled={!canEdit}
            />
            {canEdit && (
              <div className="admin-edit-user-legacy__admin-note-actions">
                <Button
                  className="btn btn--sm"
                  label={isAddingNote ? 'Adding…' : 'Add note'}
                  type="button"
                  disabled={isAddingNote || newNoteContent.trim().length === 0}
                  onClick={handleAddNote}
                />
              </div>
            )}
          </div>

          {adminNotes.length === 0 ? (
            <div className="admin-edit-user-legacy__admin-note-empty">No notes yet.</div>
          ) : (
            <>
              <ul className="admin-edit-user-legacy__admin-note-list">
                {(showAllNotes ? adminNotes : adminNotes.slice(0, NOTES_INITIAL_VISIBLE)).map((note) => {
                  const isEditing = editingNoteId === note.id;
                  return (
                    <li key={note.id} className="admin-edit-user-legacy__admin-note-item">
                      <div className="admin-edit-user-legacy__admin-note-meta">
                        <span className="admin-edit-user-legacy__admin-note-author">
                          {note.authorEmail || 'unknown'}
                        </span>
                        <span className="admin-edit-user-legacy__admin-note-date">
                          {format(new Date(note.created), 'yyyy-MM-dd HH:mm')}
                          {note.updatedAt && (
                            <span className="admin-edit-user-legacy__admin-note-edited">
                              {' '}
                              · edited {format(new Date(note.updatedAt), 'yyyy-MM-dd HH:mm')}
                            </span>
                          )}
                        </span>
                        {!isEditing && canEdit && (
                          <div className="admin-edit-user-legacy__admin-note-row-actions">
                            <button
                              type="button"
                              className="action-btn"
                              title="Edit"
                              onClick={() => handleStartEditNote(note)}
                            >
                              <span className="ico--edit ico--xs" />
                            </button>
                            <button
                              type="button"
                              className="action-btn"
                              title="Delete"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <span className="ico--trash ico--xs" />
                            </button>
                          </div>
                        )}
                      </div>
                      {isEditing ? (
                        <div className="admin-edit-user-legacy__admin-note-edit">
                          <textarea
                            className="admin-edit-user-legacy__admin-note-textarea"
                            value={editingContent}
                            onChange={(e) => setEditingContent(e.target.value)}
                            maxLength={5000}
                            rows={3}
                            autoFocus
                          />
                          <div className="admin-edit-user-legacy__admin-note-edit-actions">
                            <Button
                              className="btn btn--sm btn--secondary"
                              label="Cancel"
                              type="button"
                              onClick={handleCancelEditNote}
                              disabled={isSavingEdit}
                            />
                            <Button
                              className="btn btn--sm"
                              label={isSavingEdit ? 'Saving…' : 'Save'}
                              type="button"
                              onClick={handleSaveEditNote}
                              disabled={
                                isSavingEdit ||
                                editingContent.trim().length === 0 ||
                                editingContent === note.content
                              }
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="admin-edit-user-legacy__admin-note-content">{note.content}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {!showAllNotes && adminNotes.length > NOTES_INITIAL_VISIBLE && (
                <div className="admin-edit-user-legacy__admin-note-show-more">
                  <button
                    type="button"
                    className="admin-edit-user-legacy__admin-note-show-more-btn"
                    onClick={() => setShowAllNotes(true)}
                  >
                    Show more ({adminNotes.length - NOTES_INITIAL_VISIBLE})
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="admin-edit-user-legacy__accounts">
          <div className="page-box__title-action">
            <div className="page-box__title">
              Invited users
              {canEdit && (
                <Button
                  className="btn btn--sm"
                  label="Add user"
                  type="button"
                  onClick={() => inviteUser()}
                />
              )}
            </div>
          </div>
          <div className="admin-edit-user-legacy__invited invited-table">
            {invitedUser.length > 0 ? (
              <>
                <div className="invited-table__row invited-table__row--header">
                  <div>User ID</div>
                  <div>Email</div>
                  <div>Role</div>
                  <div>Date</div>
                </div>
                {invitedUser.map((user) => (
                  // `data-label` — etykieta wartości w karcie na telefonie (nagłówek tabelki jest tam ukryty).
                  <div className="invited-table__row" key={user.userId}>
                    <div data-label="User ID">{user.userId}</div>
                    <div className="invited-table__email">{user.email}</div>
                    <div data-label="Role">{user.role}</div>
                    <div data-label="Date">{format(user.created, 'yyy-MM-dd')}</div>
                  </div>
                ))}
              </>
            ) : (
              <EmptyPanel />
            )}
          </div>
        </div>
        <div>
          <div className="page-box__title-action">
            <div className="page-box__title">Projects</div>
          </div>
          <DataTable
            stripedRows
            paginator
            paginatorClassName="pagination"
            removableSort
            sortField="id"
            sortOrder={1}
            rows={50}
            rowsPerPageOptions={[10, 25, 50]}
            value={projectsForId}
            tableStyle={{ width: '100%' }}
            tableClassName="table"
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            emptyMessage={() => <EmptyPanel />}
            currentPageReportTemplate={t('rowsNumber') + ':'}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          >
            <Column
              field="id"
              header="Project ID"
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '45px' }}
            />
            <Column
              field="projectLabel"
              header="Name"
              bodyClassName="table__cell--stack-title"
              body={(data) => (
                <NavLink to={`${PROJECTS}/${data.id}`} target="_blank">
                  {data.label}
                </NavLink>
              )}
              headerClassName="table__header"
              style={{ width: '100px' }}
            />
            <Column
              field="created"
              header="Created"
              body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '145px' }}
            />
          </DataTable>
        </div>
        <div>
          <div className="page-box__title-action">
            <div className="page-box__title">
              {t('apiKeys')}
              {canEdit && (
                <Button
                  className="btn btn--sm"
                  label={t('createApiKey')}
                  type="button"
                  onClick={() => addNewAccountApiKey()}
                />
              )}
            </div>
          </div>
          <DataTable
            stripedRows
            paginator
            paginatorClassName="pagination"
            removableSort
            sortField="created"
            sortOrder={-1}
            rows={50}
            rowsPerPageOptions={[10, 25, 50]}
            value={apiKeys}
            tableStyle={{ width: '100%' }}
            tableClassName="table"
            className="table-wrapper table-wrapper--stack"
            responsiveLayout="stack"
            emptyMessage={() => <EmptyPanel />}
            currentPageReportTemplate={t('rowsNumber') + ':'}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          >
            <Column
              field="key"
              header={t('apiKey')}
              bodyClassName="table__cell--stack-title"
              body={(apiKey: ApiKeyResponse) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {apiKey.key.substring(0, 20)}...
                  </span>
                  <Button
                    className="action-btn"
                    type="button"
                    onClick={() => copyToClipboard(apiKey.key)}
                    title={t('copy')}
                  >
                    <span className="ico--copy ico--xs" />
                  </Button>
                </div>
              )}
              headerClassName="table__header"
              style={{ minWidth: '100px' }}
            />
            <Column
              field="description"
              header={t('description')}
              body={(apiKey: ApiKeyResponse) => apiKey.description || '-'}
              headerClassName="table__header"
            />
            <Column
              field="status"
              header={t('status')}
              body={(apiKey: ApiKeyResponse) => (
                <span className={`status-${apiKey.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                  {apiKey.status === 'ACTIVE' ? t('active') : t('inactive')}
                </span>
              )}
              headerClassName="table__header"
              style={{ minWidth: '50px' }}
            />
            <Column
              field="expires"
              header={t('validity')}
              sortable
              body={(apiKey: ApiKeyResponse) => {
                const { status, label } = getKeyStatus(apiKey.expires);
                return <span className={`status-${status}`}>{label}</span>;
              }}
              headerClassName="table__header"
              style={{ minWidth: '120px' }}
            />
          </DataTable>
          {toastElement}
        </div>

        <div>
          {/* API Integration Info */}
          {(userData.apiClientId ||
            userData.apiExternalAccountId ||
            userData.apiExternalAccountSettings) && (
            <div>
              <div className="page-box__title-action">
                <div className="page-box__title">{t('apiIntegration')}</div>
              </div>

              {userData.apiClientId && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiClientName')}:</div>
                    <div className="form-box__value">
                      {userData.apiClientName || `ID: ${userData.apiClientId}`}
                    </div>
                  </div>
                </div>
              )}

              {userData.apiExternalAccountId && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiExternalAccountId')}:</div>
                    <div className="form-box__value">{userData.apiExternalAccountId}</div>
                  </div>
                </div>
              )}

              {userData.apiExternalAccountSettings && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiExternalAccountSettings')}:</div>
                    <pre className="form-box__value form-box__pre">
                      {JSON.stringify(userData.apiExternalAccountSettings, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="admin-edit-user-legacy__audit-log">
          <div className="page-box__title-action">
            <div className="page-box__title">Activity log ({auditLog.length})</div>
          </div>
          {auditLog.length === 0 ? (
            <div className="admin-edit-user-legacy__audit-empty">No changes recorded yet.</div>
          ) : (
            <ul className="admin-edit-user-legacy__audit-list">
              {auditLog.map((entry) => {
                const changes = entry.details?.changes ?? [];
                const summary = auditActionSummary(entry);
                return (
                  <li key={entry.id} className="admin-edit-user-legacy__audit-item">
                    <div className="admin-edit-user-legacy__audit-head">
                      <span className="admin-edit-user-legacy__audit-action">
                        {auditActionLabel(entry.action)}
                      </span>
                      <span className="admin-edit-user-legacy__audit-date">
                        {format(new Date(entry.created), 'yyyy-MM-dd HH:mm')}
                      </span>
                    </div>
                    <div className="admin-edit-user-legacy__audit-actor">
                      by {entry.actorEmail || `user #${entry.actorUserId}`}
                    </div>
                    <ul className="admin-edit-user-legacy__audit-changes">
                      {summary && <li className="admin-edit-user-legacy__audit-change">{summary}</li>}
                      {changes.map((change: AccountFieldChange, i: number) => (
                        <li key={i} className="admin-edit-user-legacy__audit-change">
                          <span className="admin-edit-user-legacy__audit-field">
                            {auditFieldLabel(change.field)}:
                          </span>{' '}
                          {change.added || change.removed ? (
                            <span className="admin-edit-user-legacy__audit-diff">
                              {(change.added ?? []).map((v) => (
                                <span key={`a-${v}`} className="admin-edit-user-legacy__audit-added">
                                  +{v}
                                </span>
                              ))}
                              {(change.removed ?? []).map((v) => (
                                <span key={`r-${v}`} className="admin-edit-user-legacy__audit-removed">
                                  −{v}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="admin-edit-user-legacy__audit-diff">
                              <span className="admin-edit-user-legacy__audit-from">
                                {formatAuditValue(change.from)}
                              </span>
                              <span className="admin-edit-user-legacy__audit-arrow">→</span>
                              <span className="admin-edit-user-legacy__audit-to">
                                {formatAuditValue(change.to)}
                              </span>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {renderUserActivity()}
      </div>
    </div>
  );
};

export default AdminUsersEditPageLegacy;
